<script>
    import {
        CardLayout,
        DateInput,
        DecimalInput,
    } from "../../../Components/Common/index.svelte";
    import { MinAccountNameLength } from "../../../Utils/utilConstants.svelte";

    import { DoctorTitleTypes } from "../../../Utils/utilEnums.svelte";

    import {
        checkAbnValidation,
        getBankAccountNumberValidation,
        getBsbValidation,
        getMobileValidation,
    } from "../../../Utils/utilFunctions.svelte";

    export let accountDetails = {};

    $: isValidFirstName =
        accountDetails.firstName &&
        accountDetails.firstName.length >= MinAccountNameLength;

    $: isValidMiddleName = accountDetails.middleName
        ? accountDetails.middleName.length >= MinAccountNameLength
        : true;

    $: isValidLastName =
        accountDetails.lastName &&
        accountDetails.lastName.length >= MinAccountNameLength;

    $: isValidPreferredFirstName = accountDetails.preferredName
        ? accountDetails.preferredName.length >= MinAccountNameLength
        : true;

    $: isValidPhone = getMobileValidation(accountDetails.phone);

    $: isValidAhpraID = accountDetails.ahpra
        ? accountDetails.ahpra.length > 3
        : false;

    $: isValidBankAccountName = accountDetails.bankAccountName
        ? accountDetails.bankAccountName.length > 0
        : false;

    $: isValidBankBSB = getBsbValidation(accountDetails.bankAccountBSB);

    $: isValidBankAccountNo = getBankAccountNumberValidation(
        accountDetails.bankAccountNumber,
    );

    $: isValidABN = checkAbnValidation(accountDetails.abn);

    let errInputs = [];

    export const checkAccountValidation = () => {
        errInputs = [];

        _updateInvalidFieldsStyle();

        return errInputs.length == 0;
    };

    // HELPER FUNCTIONS
    const _updateInvalidFieldsStyle = () => {
        if (!accountDetails.firstName || !isValidFirstName)
            _setErrorStyle("accountFirstNameInput");
        else _resetDefaultStyle("accountFirstNameInput");

        if (!accountDetails.middleName && !isValidMiddleName)
            _setErrorStyle("accountMiddleNameInput");
        else _resetDefaultStyle("accountMiddleNameInput");

        if (!accountDetails.lastName || !isValidLastName)
            _setErrorStyle("accountLastNameInput");
        else _resetDefaultStyle("accountLastNameInput");

        if (accountDetails.preferredName && !isValidPreferredFirstName)
            _setErrorStyle("accountPreferredFirstNameInput");
        else _resetDefaultStyle("accountPreferredFirstNameInput");

        if (!accountDetails.birthDate) _setErrorStyle("accountDobInput");
        else _resetDefaultStyle("accountDobInput");

        if (!accountDetails.phone || !isValidPhone)
            _setErrorStyle("accountPhoneInput");
        else _resetDefaultStyle("accountPhoneInput");

        if (!accountDetails.ahpra || !isValidAhpraID)
            _setErrorStyle("accountAhpraInput");
        else _resetDefaultStyle("accountAhpraInput");

        if (!accountDetails.bankAccountName || !isValidBankAccountName)
            _setErrorStyle("accountBankAccountNameInput");
        else _resetDefaultStyle("accountBankAccountNameInput");

        if (!accountDetails.bankAccountBSB || !isValidBankBSB)
            _setErrorStyle("accountBankBsbInput");
        else _resetDefaultStyle("accountBankBsbInput");

        if (!accountDetails.bankAccountNumber || !isValidBankAccountNo)
            _setErrorStyle("accountBankAccountNoInput");
        else _resetDefaultStyle("accountBankAccountNoInput");

        if (!accountDetails.abn || !isValidABN)
            _setErrorStyle("accountAbnInput");
        else _resetDefaultStyle("accountAbnInput");

        if (errInputs.length) {
            const scrollToElement = document.getElementById(errInputs[0]);
            if (scrollToElement)
                scrollToElement.scrollIntoView({ behavior: "smooth" });
        }
    };

    const _setErrorStyle = (id) => {
        errInputs.push(id);
        const field = document.getElementById(`${id}`);
        field.style.outline = "red solid 2px";
    };

    const _resetDefaultStyle = (id) => {
        const field = document.getElementById(`${id}`);
        field.style.outline = "none";
    };
</script>

<CardLayout title="Account Details">
    <div class="flex flex-col gap-y-4 p-4">
        <div class="flex flex-1 gap-x-4">
            <div class="flex flex-col flex-1 gap-y-4">
                <p class="leading-6 text-lg font-bold">Person Details</p>

                <div class="flex items-center flex flex-1 gap-x-4">
                    <p class="title mandatory">Title</p>
                    <select
                        class="select select-sm select-bordered flex-1"
                        bind:value={accountDetails.title}
                    >
                        {#each DoctorTitleTypes as title}
                            <option value={title.id}>{title.type}</option>
                        {/each}
                    </select>
                </div>

                <div class="flex items-start gap-x-4">
                    <p class="title mandatory">First Name</p>
                    <div class="flex flex-col flex-1 gap-y-2">
                        <input
                            type="text"
                            class="input input-sm input-bordered"
                            id="accountFirstNameInput"
                            autocomplete="off"
                            bind:value={accountDetails.firstName}
                            on:focus={() =>
                                _resetDefaultStyle("accountFirstNameInput")}
                        />
                        {#if accountDetails.firstName && !isValidFirstName}
                            <p class="text-error">
                                Invalid first name length (Min. {MinAccountNameLength}
                                characters)
                            </p>
                        {/if}
                    </div>
                </div>

                <div class="flex items-start gap-x-4">
                    <p class="title">Middle Name(s)</p>
                    <div class="flex flex-col flex-1 gap-y-2">
                        <input
                            type="text"
                            class="input input-sm input-bordered"
                            id="accountMiddleNameInput"
                            autocomplete="off"
                            bind:value={accountDetails.middleName}
                            on:focus={() =>
                                _resetDefaultStyle("accountMiddleNameInput")}
                        />
                        {#if accountDetails.middleName && !isValidMiddleName}
                            <p class="text-error">
                                Invalid middle name length (Min. {MinAccountNameLength}
                                characters)
                            </p>
                        {/if}
                    </div>
                </div>

                <div class="flex items-start gap-x-4">
                    <p class="title mandatory title">Last Name</p>
                    <div class="flex flex-col flex-1 gap-y-2">
                        <input
                            type="text"
                            class="input input-sm input-bordered"
                            id="accountLastNameInput"
                            autocomplete="off"
                            bind:value={accountDetails.lastName}
                            on:focus={() =>
                                _resetDefaultStyle("accountLastNameInput")}
                        />
                        {#if accountDetails.lastName && !isValidLastName}
                            <p class="text-error">
                                Invalid last name length (Min. {MinAccountNameLength}
                                characters)
                            </p>
                        {/if}
                    </div>
                </div>

                <div class="flex items-start gap-x-4">
                    <p class="title">Preferred Name</p>
                    <div class="flex flex-col flex-1 gap-y-2">
                        <input
                            type="text"
                            class="input input-sm input-bordered"
                            id="accountPreferredFirstNameInput"
                            autocomplete="off"
                            bind:value={accountDetails.preferredName}
                            on:focus={() =>
                                _resetDefaultStyle(
                                    "accountPreferredFirstNameInput",
                                )}
                        />
                        {#if accountDetails.preferredName && !isValidPreferredFirstName}
                            <p class="text-error">
                                Invalid preferred name length (Min. {MinAccountNameLength}
                                characters)
                            </p>
                        {/if}
                    </div>
                </div>

                <div class="flex items-center gap-x-4">
                    <p class="title mandatory">Date of Birth</p>
                    <!-- <input
                        type="date"
                        class="input input-sm input-bordered flex-1"
                        id="accountDobInput"
                        bind:value={accountDetails.birthDate}
                        max={new Date().toISOString().split("T")[0]}
                        on:focus={() => _resetDefaultStyle("accountDobInput")}
                    /> -->
                    <DateInput
                        bind:value={accountDetails.birthDate}
                        id="accountDobInput"
                        maxDate={new Date().toISOString().split("T")[0]}
                        wrapperClass="input input-sm px-0 input-bordered w-full"
                        wrapperClassFocus="inputFocus"
                    />
                </div>

                <div class="flex items-start gap-x-4">
                    <p class="title mandatory">Mobile No.</p>
                    <div class="flex flex-col flex-1 gap-y-2">
                        <input
                            type="text"
                            class="input input-sm input-bordered"
                            id="accountPhoneInput"
                            autocomplete="off"
                            bind:value={accountDetails.phone}
                            on:focus={() =>
                                _resetDefaultStyle("accountPhoneInput")}
                        />
                        {#if accountDetails.phone && !isValidPhone}
                            <p class="text-error">Invalid phone number</p>
                        {/if}
                    </div>
                </div>
            </div>

            <div class="divider divider-horizontal" />

            <div class="flex flex-col flex-1 gap-y-4">
                <p class="leading-6 text-lg font-bold">Bank Details</p>

                <div class="flex items-start gap-x-4">
                    <p class="title mandatory">Account Name</p>
                    <div class="flex flex-col flex-1 gap-y-2">
                        <input
                            type="text"
                            class="input input-sm input-bordered"
                            id="accountBankAccountNameInput"
                            autocomplete="off"
                            bind:value={accountDetails.bankAccountName}
                            on:focus={() =>
                                _resetDefaultStyle(
                                    "accountBankAccountNameInput",
                                )}
                        />
                        {#if accountDetails.bankAccountName && !isValidBankAccountName}
                            <p class="text-error">
                                Invalid account name length (Min. 3 characters)
                            </p>
                        {/if}
                    </div>
                </div>

                <div class="flex items-start gap-x-4">
                    <p class="title mandatory">BSB No.</p>
                    <div class="flex flex-col flex-1 gap-y-2">
                        <input
                            type="text"
                            class="input input-sm input-bordered"
                            id="accountBankBsbInput"
                            autocomplete="off"
                            bind:value={accountDetails.bankAccountBSB}
                            on:focus={() =>
                                _resetDefaultStyle("accountBankBsbInput")}
                        />
                        {#if accountDetails.bankAccountBSB && !isValidBankBSB}
                            <p class="text-error">Invalid BSB number</p>
                        {/if}
                    </div>
                </div>

                <div class="flex items-start gap-x-4">
                    <p class="title mandatory">Bank Account No.</p>
                    <div class="flex flex-col flex-1 gap-y-2">
                        <input
                            type="text"
                            class="input input-sm input-bordered"
                            id="accountBankAccountNoInput"
                            autocomplete="off"
                            bind:value={accountDetails.bankAccountNumber}
                            on:focus={() =>
                                _resetDefaultStyle("accountBankAccountNoInput")}
                        />
                        {#if accountDetails.bankAccountNumber && !isValidBankAccountNo}
                            <p class="text-error">
                                Invalid bank account number
                            </p>
                        {/if}
                    </div>
                </div>

                <div class="flex items-start gap-x-4">
                    <p class="title mandatory">AHPRA ID No.</p>
                    <div class="flex flex-col flex-1 gap-y-2">
                        <div class="flex items-center gap-x-2">
                            <p class="font-bold">MED</p>
                            <input
                                type="text"
                                class="input input-sm input-bordered flex-1"
                                id="accountAhpraInput"
                                autocomplete="off"
                                bind:value={accountDetails.ahpra}
                                on:focus={() =>
                                    _resetDefaultStyle("accountAhpraInput")}
                            />
                        </div>
                        {#if accountDetails.ahpra && !isValidAhpraID}
                            <p class="text-error">Invalid AHPRA ID</p>
                        {/if}
                    </div>
                </div>

                <div class="flex items-start gap-x-4">
                    <p class="title mandatory">ABN</p>
                    <div class="flex flex-col flex-1 gap-y-2">
                        <input
                            type="text"
                            class="input input-sm input-bordered"
                            id="accountAbnInput"
                            autocomplete="off"
                            bind:value={accountDetails.abn}
                            on:focus={() =>
                                _resetDefaultStyle("accountAbnInput")}
                        />
                        {#if accountDetails.abn && !isValidABN}
                            <p class="text-error">Invalid ABN number</p>
                        {/if}
                    </div>
                </div>

                <div class="flex items-center gap-x-4">
                    <p class="title">Underpayment Threshold</p>

                    <div class="flex flex-1">
                        <DecimalInput
                            bind:value={accountDetails.underPayThreshold}
                            inputClass="w-full"
                        />
                    </div>
                </div>
            </div>
        </div>

        <div class="flex flex-1 items-center gap-x-4">
            <p class="title">Qualifications</p>
            <input
                type="text"
                class="input input-sm input-bordered flex-1"
                id="accountQualificationInput"
                autocomplete="off"
                placeholder="e.g. MBBS FANZCA"
                bind:value={accountDetails.qualifications}
                on:focus={() => _resetDefaultStyle("accountQualificationInput")}
            />
        </div>

        <div class="flex flex-1 items-center">
            <p class="title">Claim Delay Hours</p>
            <div class="flex flex-1 items-center gap-x-4">
                <input
                    type="range"
                    class="range range-secondary flex-1"
                    min="0"
                    max="72"
                    step="1"
                    bind:value={accountDetails.claimDelayHours}
                />
                <p class="text-right w-24">
                    {accountDetails.claimDelayHours} hours
                </p>
            </div>
        </div>

        <div class="flex flex-1 gap-x-4">
            <div class="flex items-center flex-1">
                <p class="title">Calendar Colour</p>
                <input
                    type="color"
                    class="input max-w-xs input-xs w-32"
                    autocomplete="off"
                    bind:value={accountDetails.calendarColour}
                />
            </div>

            <div class="divider divider-horizontal" />

            <div class="flex items-center flex-1">
                <p class="title">Account Status</p>
                <select
                    class="select select-sm select-bordered flex-1"
                    bind:value={accountDetails.status}
                >
                    <option value={Number(0)}>Active</option>
                    <option value={Number(1)}>Demo</option>
                    <option value={Number(5)}>new</option>
                </select>
            </div>
        </div>
    </div>
</CardLayout>

<style>
    .mandatory::after {
        content: " * ";
    }

    .title {
        line-height: 1.5rem;
        font-size: 1rem;
        font-weight: 700;
        min-width: 10rem;
    }
</style>
